import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useModal } from "../../contexts/ModalContext";

function FAQAccordion() {
    const [openIndex, setOpenIndex] = useState(-1); // No item open by default

    const faqs = [
        {
            question: "What is VAJRA?",
            answer: "VAJRA is a comprehensive ESG and sustainability reporting platform that streamlines data collection, analysis, and reporting in alignment with frameworks such as GRI and BRSR."
        },
        {
            question: "How can VAJRA support my organization?",
            answer: "VAJRA simplifies complex reporting processes, enhances data accuracy, and saves time by providing a centralized platform for ESG data management, enabling better decision-making and improved stakeholder transparency."
        },
        {
            question: "How can I enter data into VAJRA?",
            answer: "Data can be entered through two methods: Manual Entry - Available across all modules, and Bulk Upload - Available for environmental data, including GHG accounting."
        },
        {
            question: "Can multiple users access VAJRA?",
            answer: "Yes. VAJRA allows multiple users within an organization to access the platform with defined roles and permissions, ensuring secure and efficient collaboration."
        },
        {
            question: "How does VAJRA support GHG accounting and water management?",
            answer: "VAJRA uses globally accepted methodologies for GHG calculations and enables tracking of water consumption, sources, and recycling data, supporting compliance with regulatory frameworks."
        },
        {
            question: "How secure is the data on VAJRA?",
            answer: "Data security is a top priority. VAJRA offers two-factor authentication, end-to-end encryption, role-based access control, regular security audits, and compliance with data protection regulations. It is cloud-agnostic and can be deployed on preferred infrastructure if required."
        }
    ];

    const toggleAccordion = (index) => {
        setOpenIndex(openIndex === index ? -1 : index);
    };

    return (
        <div className="space-y-4">
            {faqs.map((faq, index) => (
                <div key={index} className="pb-4 border-b border-gray-300">
                    <button
                        onClick={() => toggleAccordion(index)}
                        className="flex items-center justify-between w-full text-left"
                    >
                        <h3 className="text-base font-semibold text-gray-800 md:text-lg montserrat">
                            {faq.question}
                        </h3>
                        <span className="flex-shrink-0 ml-4">
                            <div className="flex items-center justify-center w-6 h-6 border-2 border-orange-500 rounded-full">
                                {openIndex === index ? (
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M2 6H10" stroke="#F97316" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                ) : (
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6 2V10M2 6H10" stroke="#F97316" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                )}
                            </div>
                        </span>
                    </button>
                    {openIndex === index && (
                        <p className="mt-3 text-sm leading-relaxed text-gray-600 md:text-base montserrat">
                            {faq.answer}
                        </p>
                    )}
                </div>
            ))}
        </div>
    );
}

function WhoItsBuiltForAccordion() {
    const [openIndex, setOpenIndex] = useState(0); // Default to first item open

    const audiences = [
        {
            title: "MSMEs Beginning Their ESG Journey",
            description: "Get started with ESG in a cost-effective and simple way. Vajra makes it easy for small and medium enterprises to track data, generate reports, and meet compliance without heavy investments."
        },
        {
            title: "Large Enterprises",
            description: "Manage ESG across multiple Facilities and global supply chains. Vajra provides a centralized view for better benchmarking, decision-making, and reporting."
        },
        {
            title: "Suppliers to MNCs",
            description: "Simplify data collection and compliance reporting to meet buyer requirements. Vajra ensures suppliers stay audit-ready and aligned with international ESG standards."
        },
        {
            title: "Companies Preparing for ESG Ratings & IPOs",
            description: "Strengthen disclosure and transparency with audit-ready reports aligned to BRSR, GRI, and global frameworks. Vajra helps businesses demonstrate responsible governance and sustainability performance to investors and regulators."
        }
    ];

    const toggleAccordion = (index) => {
        setOpenIndex(openIndex === index ? -1 : index);
    };

    return (
        <div className="space-y-4">
            {audiences.map((audience, index) => (
                <div key={index} className="pb-4 border-b border-gray-400">
                    <button
                        onClick={() => toggleAccordion(index)}
                        className="flex items-center justify-between w-full text-left"
                    >
                        <h3 className="text-lg font-medium text-white md:text-xl montserrat">
                            {audience.title}
                        </h3>
                        <span className="flex-shrink-0 ml-4 text-2xl text-white">
                            {openIndex === index ? (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M19 9L12 16L5 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            ) : (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            )}
                        </span>
                    </button>
                    {openIndex === index && audience.description && (
                        <p className="mt-3 text-sm text-gray-200 md:text-base montserrat">
                            {audience.description}
                        </p>
                    )}
                </div>
            ))}
        </div>
    );
}

function Vajra() {
    const { openModal } = useModal();

    const handleBookDemo = () => {
        console.log('Book Demo clicked, attempting to open modal...');
        try {
            openModal();
            console.log('Modal opened successfully');
        } catch (error) {
            console.error('Error opening modal:', error);
            // Fallback to navigation if modal fails
            window.location.href = '/contact-us';
        }
    };

    return (
        <div className="flex flex-col justify-center text-white bg-white">
            <Helmet>
                <title>ESG Reporting Software for Listed Companies | Vajra by Nirantara</title>
                <meta name="description" content="Vajra by Nirantara is an ESG reporting software platform for listed companies, automating Scope 1, 2 & 3 emissions tracking, GHG calculations, and sustainability disclosures to keep you audit-ready and compliant." />
                <meta name="keywords" content="ESG Reporting Software for Listed Companies, ESG management platform, GHG emissions calculation platform for listed companies, ESG data reporting solutions, automated sustainability reporting platform, automated ESG reporting services, ESG reporting software and consulting, Scope 1 2 3 emissions tracking software" />
                <link rel="canonical" href="https://nirantara.solutions/vajra" />
            </Helmet>
            <div className="flex flex-col justify-center text-white bg-white">
                <div className="flex flex-col justify-center w-full bg-white max-md:max-w-full">
                    <section className="flex overflow-hidden relative flex-col justify-end items-start px-8 md:px-16 lg:px-24 pb-16 md:pb-20 pt-32 w-full min-h-[calc(100vh)] max-md:px-5 max-md:pb-12 max-md:max-w-full">
                        <img
                            loading="lazy"
                            src="/vajra-4.jpg"
                            alt="Vajra Hero Background"
                            className="absolute inset-0 object-cover size-full"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
                        <div className="flex z-10 relative flex-col items-start w-full max-w-[600px] max-md:max-w-full">
                            <h1 className="sr-only">ESG Reporting Software for Listed Companies</h1>
                            <p className="text-4xl font-black leading-tight text-left text-white uppercase md:text-5xl lg:text-6xl max-md:text-3xl helvetica">
                                VAJRA
                            </p>
                            <button
                                onClick={handleBookDemo}
                                className="flex items-center gap-3 px-8 py-4 mt-6 text-base font-medium text-white transition-all duration-300 rounded-lg md:text-lg hover:bg-opacity-90"
                                style={{ backgroundColor: '#2C5F7C' }}
                            >
                                <span className="montserrat">Book A Demo</span>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4 10H16M16 10L10 4M16 10L10 16" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </div>
                    </section>

                    {/* Measure, Manage And Monetize Section */}
                    <section className="w-full py-16 bg-gray-50 md:py-24" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <h2 className="mb-8 text-black" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px', textTransform: 'capitalize' }}>
                                Measure, Manage And Monetize
                            </h2>
                            <div className="space-y-6 text-gray-700">
                                <p style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                    Vajra ESG by Nirantara is a next-generation ESG intelligence platform that enables organizations to Measure manage and monitize their environmental, social, and governance (ESG) performance. Designed for MSMEs, large enterprises, and global supply chains, Vajra transforms complex sustainability requirements into clear, actionable insights.
                                </p>
                                <p style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                    With Vajra, businesses can go beyond compliance and turn ESG into a strategic advantage. The platform simplifies data collection, integrates seamlessly with ERP systems, and provides real-time dashboards to track performance across Scope 1, Scope 2, and Scope 3 emissions, energy, water, waste, diversity, equity & inclusion (DEI), labor practices, governance, and more.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Features of Vajra Section */}
                    <section className="w-full py-16 bg-white md:py-24" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <h2 className="mb-4 text-center text-black" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px', textTransform: 'capitalize' }}>
                                Features of Vajra
                            </h2>
                            <p className="mb-12 text-center text-gray-600 md:mb-16" style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                What the platform can do
                            </p>

                            <div className="grid grid-cols-1 gap-6 mb-12 md:grid-cols-2 lg:grid-cols-3 md:gap-8">
                                {/* Feature Card 1 */}
                                <div className="flex flex-col items-center p-6 text-center rounded-lg bg-orange-50 md:p-8">
                                    <div className="flex items-center justify-center w-24 h-24 p-4 mb-6 overflow-hidden rounded-2xl">
                                        <img
                                            src="/simplifieddata.png"
                                            alt="Simplified Data Management"
                                            className="object-contain w-full h-full"
                                        />
                                    </div>
                                    <h3 className="mb-3 text-xl font-semibold text-gray-800 montserrat">Simplified Data Management</h3>
                                    <p className="text-sm leading-relaxed text-gray-600 montserrat">
                                        Centralize ESG data on a single platform to streamline management, ensure accuracy, maintain compliance, and boost team efficiency.
                                    </p>
                                </div>

                                {/* Feature Card 2 */}
                                <div className="flex flex-col items-center p-6 text-center rounded-lg bg-orange-50 md:p-8">
                                    <div className="flex items-center justify-center w-24 h-24 p-4 mb-6 overflow-hidden rounded-2xl">
                                        <img
                                            src="/unlimited users.png"
                                            alt="Unlimited Users & Facilities"
                                            className="object-contain w-full h-full"
                                        />
                                    </div>
                                    <h3 className="mb-3 text-xl font-semibold text-gray-800 montserrat">Unlimited Users & Facilities</h3>
                                    <p className="text-sm leading-relaxed text-gray-600 montserrat">
                                        Scale effortlessly across multiple teams, departments, and locations without worrying about user or facility limits.
                                    </p>
                                </div>

                                {/* Feature Card 3 */}
                                <div className="flex flex-col items-center p-6 text-center rounded-lg bg-orange-50 md:p-8">
                                    <div className="flex items-center justify-center w-24 h-24 p-4 mb-6 overflow-hidden rounded-2xl">
                                        <img
                                            src="/automated.png"
                                            alt="Automated Reporting"
                                            className="object-contain w-full h-full"
                                        />
                                    </div>
                                    <h3 className="mb-3 text-xl font-semibold text-gray-800 montserrat">Automated Reporting</h3>
                                    <p className="text-sm leading-relaxed text-gray-600 montserrat">
                                        Generate audit-ready ESG reports instantly, reduce manual effort, and provide actionable insights for executives, regulators, and stakeholders.
                                    </p>
                                </div>

                                {/* Feature Card 4 */}
                                <div className="flex flex-col items-center p-6 text-center rounded-lg bg-orange-50 md:p-8">
                                    <div className="flex items-center justify-center w-24 h-24 p-4 mb-6 overflow-hidden rounded-2xl">
                                        <img
                                            src="/seemlesserp.png"
                                            alt="Seamless ERP Compatibility"
                                            className="object-contain w-full h-full"
                                        />
                                    </div>
                                    <h3 className="mb-3 text-xl font-semibold text-gray-800 montserrat">Seamless ERP Compatibility</h3>
                                    <p className="text-sm leading-relaxed text-gray-600 montserrat">
                                        Access live dashboards to monitor ESG metrics as they happen, Visualize performance trends, identify risks, and make data-driven decisions quickly.
                                    </p>
                                </div>

                                {/* Feature Card 5 */}
                                <div className="flex flex-col items-center p-6 text-center rounded-lg bg-orange-50 md:p-8">
                                    <div className="flex items-center justify-center w-24 h-24 p-4 mb-6 overflow-hidden rounded-2xl">
                                        <img
                                            src="/realtimedashboards.png"
                                            alt="Real-Time Dashboards"
                                            className="object-contain w-full h-full"
                                        />
                                    </div>
                                    <h3 className="mb-3 text-xl font-semibold text-gray-800 montserrat">Real-Time Dashboards</h3>
                                    <p className="text-sm leading-relaxed text-gray-600 montserrat">
                                        Access live dashboards to monitor ESG metrics as they happen, Visualize performance trends, identify risks, and make data-driven decisions quickly.
                                    </p>
                                </div>

                                {/* Feature Card 6 */}
                                <div className="flex flex-col items-center p-6 text-center rounded-lg bg-orange-50 md:p-8">
                                    <div className="flex items-center justify-center w-24 h-24 p-4 mb-6 overflow-hidden rounded-2xl">
                                        <img
                                            src="/valuechain.png"
                                            alt="Value Chain Data Integration"
                                            className="object-contain w-full h-full"
                                        />
                                    </div>
                                    <h3 className="mb-3 text-xl font-semibold text-gray-800 montserrat">Value Chain Data Integration</h3>
                                    <p className="text-sm leading-relaxed text-gray-600 montserrat">
                                        Capture supplier and vendor data to assess risks, ensure compliance, and optimize sustainability performance across the entire supply chain.
                                    </p>
                                </div>
                            </div>

                            {/* Book A Demo Button */}
                            <div className="flex justify-center">
                                <button
                                    onClick={handleBookDemo}
                                    className="flex items-center gap-3 px-8 py-4 text-base font-medium text-white transition-all duration-300 rounded-lg md:text-lg hover:bg-opacity-90"
                                    style={{ backgroundColor: '#2C5F7C' }}
                                >
                                    <span className="montserrat">Book A Demo</span>
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M4 10H16M16 10L10 4M16 10L10 16" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </section>

                    {/* Powerful Dashboards Section */}
                    <section className="w-full py-16 bg-gray-50 md:py-24" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <h2 className="mb-16 text-center text-black md:mb-20" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px', textTransform: 'capitalize' }}>
                                Powerful Dashboards
                            </h2>

                            {/* Environment Dashboard */}
                            <div className="grid items-center grid-cols-1 gap-8 mb-16 lg:grid-cols-2 lg:gap-12 md:mb-24">
                                <div className="relative order-2 lg:order-1">
                                    <div className="absolute bottom-0 w-3/4 transform -translate-x-1/2 rounded-full left-1/2 h-3/4" style={{ backgroundColor: '#EB7E0E66', backdropFilter: 'blur(100px)', filter: 'blur(100px)', zIndex: 0 }}></div>
                                    <img src="/dashboard-1.png" alt="Environment Dashboard" className="relative z-10 w-full rounded-lg shadow-2xl" />
                                </div>
                                <div className="order-1 lg:order-2">
                                    <h3 className="mb-4 text-2xl font-semibold text-black md:text-3xl lg:text-4xl" style={{ fontFamily: 'Poppins', fontWeight: 600 }}>
                                        Environment
                                    </h3>
                                    <p className="text-base leading-relaxed text-gray-600 md:text-lg montserrat">
                                        Monitor and manage your environmental footprint with precision. Vajra tracks emissions, energy consumption, water usage, and waste generation, helping you identify hotspots, cut costs, and reduce your impact on the planet.
                                    </p>
                                </div>
                            </div>

                            {/* Social Dashboard */}
                            <div className="grid items-center grid-cols-1 gap-8 mb-16 lg:grid-cols-2 lg:gap-12 md:mb-24">
                                <div className="order-1 lg:order-1">
                                    <h3 className="mb-4 text-2xl font-semibold text-black md:text-3xl lg:text-4xl" style={{ fontFamily: 'Poppins', fontWeight: 600 }}>
                                        Social
                                    </h3>
                                    <p className="text-base leading-relaxed text-gray-600 md:text-lg montserrat">
                                        Stay accountable to people and communities. Track diversity, equity, and inclusion (DEI) metrics, labor practices, employee well-being, and community engagement to build a stronger, more responsible organization.
                                    </p>
                                </div>
                                <div className="relative order-2 lg:order-2">
                                    <div className="absolute bottom-0 w-3/4 transform -translate-x-1/2 rounded-full left-1/2 h-3/4" style={{ backgroundColor: '#EB7E0E66', backdropFilter: 'blur(100px)', filter: 'blur(100px)', zIndex: 0 }}></div>
                                    <img src="/dashboard-2.png" alt="Social Dashboard" className="relative z-10 w-full rounded-lg shadow-2xl" />
                                </div>
                            </div>

                            {/* Governance Dashboard */}
                            <div className="grid items-center grid-cols-1 gap-8 mb-16 lg:grid-cols-2 lg:gap-12 md:mb-24">
                                <div className="relative order-2 lg:order-1">
                                    <div className="absolute bottom-0 w-3/4 transform -translate-x-1/2 rounded-full left-1/2 h-3/4" style={{ backgroundColor: '#EB7E0E66', backdropFilter: 'blur(100px)', filter: 'blur(100px)', zIndex: 0 }}></div>
                                    <img src="/dashboard-3.png" alt="Governance Dashboard" className="relative z-10 w-full rounded-lg shadow-2xl" />
                                </div>
                                <div className="order-1 lg:order-2">
                                    <h3 className="mb-4 text-2xl font-semibold text-black md:text-3xl lg:text-4xl" style={{ fontFamily: 'Poppins', fontWeight: 600 }}>
                                        Governance
                                    </h3>
                                    <p className="text-base leading-relaxed text-gray-600 md:text-lg montserrat">
                                        Strengthen governance with policy tracking, compliance oversight, and risk management tools. Ensure transparency, accountability, and alignment with global ESG frameworks at every level.
                                    </p>
                                </div>
                            </div>

                            {/* Centralized Overview Dashboard */}
                            <div className="grid items-center grid-cols-1 gap-8 mb-12 lg:grid-cols-2 lg:gap-12">
                                <div className="order-1 lg:order-1">
                                    <h3 className="mb-4 text-2xl font-semibold text-black md:text-3xl lg:text-4xl" style={{ fontFamily: 'Poppins', fontWeight: 600 }}>
                                        Centralized Overview
                                    </h3>
                                    <p className="text-base leading-relaxed text-gray-600 md:text-lg montserrat">
                                        Bring it all together with a comprehensive ESG dashboard. Get a 360° view across environment, social, and governance pillars, benchmark performance, and make informed decisions backed by real data.
                                    </p>
                                </div>
                                <div className="relative order-2 lg:order-2">
                                    <div className="absolute bottom-0 w-3/4 transform -translate-x-1/2 rounded-full left-1/2 h-3/4" style={{ backgroundColor: '#EB7E0E66', backdropFilter: 'blur(100px)', filter: 'blur(100px)', zIndex: 0 }}></div>
                                    <img src="/dashboard-4.png" alt="Centralized Overview Dashboard" className="relative z-10 w-full rounded-lg shadow-2xl" />
                                </div>
                            </div>

                            {/* Book A Demo Button */}
                            <div className="flex justify-center mt-12 md:mt-16">
                                <button
                                    onClick={handleBookDemo}
                                    className="flex items-center gap-3 px-8 py-4 text-base font-medium text-white transition-all duration-300 rounded-lg md:text-lg hover:bg-opacity-90"
                                    style={{ backgroundColor: '#2C5F7C' }}
                                >
                                    <span className="montserrat">Book A Demo</span>
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M4 10H16M16 10L10 4M16 10L10 16" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </section>

                    {/* What you get from it Section */}
                    <section className="w-full py-16 bg-white md:py-24" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <h2 className="mb-16 text-center text-black md:mb-20" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px', textTransform: 'capitalize' }}>
                                What you get from it?
                            </h2>

                            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 md:gap-10">
                                {/* Benefit Card 1 */}
                                <div className="flex flex-col items-center p-6 text-center border-b border-r border-gray-200">
                                    <div className="mb-6">
                                        <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <h3 className="mb-3 text-xl font-semibold text-black montserrat">Save Time & Effort</h3>
                                    <p className="text-sm leading-relaxed text-gray-600 montserrat">
                                        Automate ESG data collection, monitoring, and reporting to reduce manual work and enhance team efficiency.
                                    </p>
                                </div>

                                {/* Benefit Card 2 */}
                                <div className="flex flex-col items-center p-6 text-center border-b border-r border-gray-200">
                                    <div className="mb-6">
                                        <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    </div>
                                    <h3 className="mb-3 text-xl font-semibold text-black montserrat">No More Reporting Stress</h3>
                                    <p className="text-sm leading-relaxed text-gray-600 montserrat">
                                        Generate accurate, audit-ready ESG reports instantly with pre-built templates and automation for full compliance.
                                    </p>
                                </div>

                                {/* Benefit Card 3 */}
                                <div className="flex flex-col items-center p-6 text-center border-b border-gray-200">
                                    <div className="mb-6">
                                        <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                        </svg>
                                    </div>
                                    <h3 className="mb-3 text-xl font-semibold text-black montserrat">Data That Drives Action</h3>
                                    <p className="text-sm leading-relaxed text-gray-600 montserrat">
                                        Transform ESG data into analytics and dashboards that highlight trends, risks, and opportunities for smarter decisions.
                                    </p>
                                </div>

                                {/* Benefit Card 4 */}
                                <div className="flex flex-col items-center p-6 text-center border-r border-gray-200">
                                    <div className="mb-6">
                                        <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                        </svg>
                                    </div>
                                    <h3 className="mb-3 text-xl font-semibold text-black montserrat">Enhanced Stakeholder Trust</h3>
                                    <p className="text-sm leading-relaxed text-gray-600 montserrat">
                                        Deliver transparent, verified ESG metrics that enhance credibility with investors, regulators, employees, and customers.
                                    </p>
                                </div>

                                {/* Benefit Card 5 */}
                                <div className="flex flex-col items-center p-6 text-center border-r border-gray-200">
                                    <div className="mb-6">
                                        <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                        </svg>
                                    </div>
                                    <h3 className="mb-3 text-xl font-semibold text-black montserrat">Accelerate Global Growth</h3>
                                    <p className="text-sm leading-relaxed text-gray-600 montserrat">
                                        Expand ESG initiatives seamlessly across teams, departments, and global locations without data inconsistencies.
                                    </p>
                                </div>

                                {/* Benefit Card 6 */}
                                <div className="flex flex-col items-center p-6 text-center">
                                    <div className="mb-6">
                                        <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <h3 className="mb-3 text-xl font-semibold text-black montserrat">Simple & User-Friendly</h3>
                                    <p className="text-sm leading-relaxed text-gray-600 montserrat">
                                        Empower every employee to manage ESG data with ease, regardless of technical skill.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Who It's Built For Section */}
                    <section className="w-full py-16 md:py-24" style={{ paddingLeft: '8%', paddingRight: '8%', backgroundColor: '#2C5F7C' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <h2 className="mb-16 text-3xl font-semibold text-center text-white md:text-4xl lg:text-5xl md:mb-20" style={{ fontFamily: 'Poppins', fontWeight: 600 }}>
                                Who It's Built For?
                            </h2>

                            <div className="grid items-center grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
                                <WhoItsBuiltForAccordion />
                                <div className="w-full h-full">
                                    <img
                                        src="/vajra-5.jpg"
                                        alt="Industrial facility"
                                        className="object-cover w-full h-full rounded-lg shadow-2xl"
                                    />
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* What Makes Us Different Section */}
                    <section className="w-full py-16 md:py-24" style={{ paddingLeft: '8%', paddingRight: '8%', backgroundColor: '#E8EEF2' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <h2 className="mb-16 text-3xl font-bold text-center text-black md:text-4xl lg:text-5xl md:mb-20" style={{ fontFamily: 'Poppins', fontWeight: 700 }}>
                                What Makes Us Different
                            </h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10 max-w-[1100px] mx-auto">
                                {/* Differentiator 1 */}
                                <div className="flex items-start gap-5">
                                    <div className="flex-shrink-0 mt-1">
                                        <div className="flex items-center justify-center overflow-hidden rounded-lg w-14 h-14">
                                            <img
                                                src="/nosubscription.png"
                                                alt="No Subscription"
                                                className="object-cover w-full h-full"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="mb-2 text-xl font-bold text-gray-800 montserrat" style={{ color: '#2C3E50' }}>
                                            No Subscription Fees, No Hidden Costs
                                        </h3>
                                        <p className="text-sm leading-relaxed text-gray-600 montserrat" style={{ color: '#6B7280' }}>
                                            Eliminate subscription overheads with a clear, one-time cost structure.
                                        </p>
                                    </div>
                                </div>

                                {/* Differentiator 2 */}
                                <div className="flex items-start gap-5">
                                    <div className="flex-shrink-0 mt-1">
                                        <div className="flex items-center justify-center overflow-hidden rounded-lg w-14 h-14">
                                            <img
                                                src="/builtontrust.png"
                                                alt="Built on Trust"
                                                className="object-cover w-full h-full"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="mb-2 text-xl font-bold text-gray-800 montserrat" style={{ color: '#2C3E50' }}>
                                            Built on Trust & Transparency
                                        </h3>
                                        <p className="text-sm leading-relaxed text-gray-600 montserrat" style={{ color: '#6B7280' }}>
                                            Demonstrate compliance and strengthen relationships through reliable, transparent reporting.
                                        </p>
                                    </div>
                                </div>

                                {/* Differentiator 3 */}
                                <div className="flex items-start gap-5">
                                    <div className="flex-shrink-0 mt-1">
                                        <div className="flex items-center justify-center overflow-hidden rounded-lg w-14 h-14">
                                            <img
                                                src="/secure.png"
                                                alt="Secure & Reliable"
                                                className="object-cover w-full h-full"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="mb-2 text-xl font-bold text-gray-800 montserrat" style={{ color: '#2C3E50' }}>
                                            Secure & Reliable
                                        </h3>
                                        <p className="text-sm leading-relaxed text-gray-600 montserrat" style={{ color: '#6B7280' }}>
                                            Protect sensitive ESG data with trusted, industry-standard security and resilience.
                                        </p>
                                    </div>
                                </div>

                                {/* Differentiator 4 */}
                                <div className="flex items-start gap-5">
                                    <div className="flex-shrink-0 mt-1">
                                        <div className="flex items-center justify-center overflow-hidden rounded-lg w-14 h-14">
                                            <img
                                                src="/backedbyexperts.png"
                                                alt="Backed by Experts"
                                                className="object-cover w-full h-full"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="mb-2 text-xl font-bold text-gray-800 montserrat" style={{ color: '#2C3E50' }}>
                                            Backed by Experts
                                        </h3>
                                        <p className="text-sm leading-relaxed text-gray-600 montserrat" style={{ color: '#6B7280' }}>
                                            Achieve meaningful sustainability outcomes with guidance from experienced ESG professionals.
                                        </p>
                                    </div>
                                </div>

                                {/* Differentiator 5 */}
                                <div className="flex items-start gap-5">
                                    <div className="flex-shrink-0 mt-1">
                                        <div className="flex items-center justify-center overflow-hidden rounded-lg w-14 h-14">
                                            <img
                                                src="/builtforall.png"
                                                alt="Built for All Business Sizes"
                                                className="object-cover w-full h-full"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="mb-2 text-xl font-bold text-gray-800 montserrat" style={{ color: '#2C3E50' }}>
                                            Built for All Business Sizes
                                        </h3>
                                        <p className="text-sm leading-relaxed text-gray-600 montserrat" style={{ color: '#6B7280' }}>
                                            Scales seamlessly from startups to global enterprises, with no limits on users, facilities, or data.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Be the Change Section */}
                    <section className="w-full py-16 bg-white md:py-24" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <h2 className="mb-16 text-3xl font-bold text-center text-black md:text-4xl lg:text-5xl md:mb-20" style={{ fontFamily: 'Poppins', fontWeight: 700 }}>
                                Be the Change
                            </h2>

                            <div className="grid grid-cols-1 gap-6 md:grid-cols-3 lg:gap-8">
                                {/* Left Card - Turn operations into opportunity */}
                                <div className="rounded-3xl overflow-hidden relative min-h-[400px] group">
                                    <img
                                        src="/card-1.png"
                                        alt="Turn operations into opportunity"
                                        className="absolute inset-0 object-cover w-full h-full"
                                    />
                                    <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                                    <div className="absolute bottom-0 left-0 right-0 p-8">
                                        <h3 className="text-2xl font-bold text-white md:text-3xl montserrat">
                                            Turn operations<br />into opportunity
                                        </h3>
                                    </div>
                                </div>

                                {/* Middle Card - Convert data into value */}
                                <div className="rounded-3xl overflow-hidden relative min-h-[400px] group">
                                    <img
                                        src="/card-2.jpg"
                                        alt="Convert data into value"
                                        className="absolute inset-0 object-cover w-full h-full"
                                    />
                                    <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                                    <div className="absolute bottom-0 left-0 right-0 p-8">
                                        <h3 className="text-2xl font-bold text-white md:text-3xl montserrat">
                                            Convert data<br />into value
                                        </h3>
                                    </div>
                                </div>

                                {/* Right Card - Lead sustainability with purpose */}
                                <div className="rounded-3xl overflow-hidden relative min-h-[400px] group">
                                    <img
                                        src="/card-3.jpg"
                                        alt="Lead sustainability with purpose"
                                        className="absolute inset-0 object-cover w-full h-full"
                                    />
                                    <div className="absolute inset-0 bg-black bg-opacity-40"></div>
                                    <div className="absolute bottom-0 left-0 right-0 p-8">
                                        <h3 className="text-2xl font-bold text-white md:text-3xl montserrat">
                                            Lead<br />sustainability<br />with purpose
                                        </h3>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* FAQ Section */}
                    <section className="w-full py-16 bg-gray-50 md:py-24" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
                                <div>
                                    <h2 className="mb-6 text-3xl font-bold text-black md:text-4xl lg:text-5xl" style={{ fontFamily: 'Poppins', fontWeight: 700 }}>
                                        Frequently Asked Questions
                                    </h2>
                                    <p className="text-base leading-relaxed text-gray-600 montserrat md:text-lg">
                                        Find answers to common questions about VAJRA, our comprehensive ESG and sustainability reporting platform. Learn how VAJRA can help your organization streamline data management and improve stakeholder transparency.
                                    </p>
                                </div>
                                <div>
                                    <FAQAccordion />
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Final CTA Section */}
                    <section className="w-full py-16 bg-gray-100 md:py-24" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <div className="grid items-center grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
                                <div>
                                    <h2 className="mb-6 text-3xl font-bold text-black md:text-4xl lg:text-5xl" style={{ fontFamily: 'Poppins', fontWeight: 700 }}>
                                        Ready to Simplifying Your ESG Reporting?
                                    </h2>
                                    <p className="mb-8 text-base leading-relaxed text-gray-600 montserrat md:text-lg">
                                        Let's Book a Demo to get better understanding of how it works & cost
                                    </p>
                                    <button
                                        onClick={handleBookDemo}
                                        className="flex items-center gap-3 px-8 py-4 mb-12 text-base font-medium text-white transition-all duration-300 rounded-lg md:text-lg hover:bg-opacity-90"
                                        style={{ backgroundColor: '#2C5F7C' }}
                                    >
                                        <span className="montserrat">Request a Demo</span>
                                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M4 10H16M16 10L10 4M16 10L10 16" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </button>
                                </div>
                                <div className="flex justify-center lg:justify-end">
                                    <img
                                        src="/vajra-6.png"
                                        alt="ESG Reporting Demo"
                                        className="object-contain w-full h-auto max-w-md"
                                    />
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}

export default Vajra;

